import styled from 'styled-components';

export const NewsBoxContainer = styled.aside<{ $isExpanded: boolean }>`
  position: fixed;
  left: 24px;
  bottom: 16px;
  isolation: isolate;
  width: clamp(340px, 36vw, 560px);
  max-height: ${({ $isExpanded }) => ($isExpanded ? 'min(58vh, 640px)' : '88px')};
  border: 1px solid
    ${({ theme }) => (theme.name === 'short' ? 'rgba(255, 255, 255, 0.28)' : 'rgba(0, 0, 0, 0.2)')};
  border-radius: 14px;
  background: ${({ theme }) =>
    theme.name === 'short'
      ? 'linear-gradient(180deg, rgba(255, 255, 255, 0.11) 0%, rgba(255, 255, 255, 0.05) 100%), rgba(0, 0, 255, 0.97)'
      : 'linear-gradient(180deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.03) 100%), rgba(255, 204, 213, 0.97)'};
  box-shadow:
    inset 0 1px 0 ${({ theme }) => (theme.name === 'short' ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.1)')},
    0 18px 38px ${({ theme }) => (theme.name === 'short' ? 'rgba(0, 0, 0, 0.28)' : 'rgba(0, 0, 0, 0.14)')};
  backdrop-filter: blur(2px);
  z-index: 2600;
  color: ${({ theme }) => theme.text};
  overflow: hidden;
  transition:
    max-height 0.24s ease,
    box-shadow 0.2s ease;
`;

export const NewsBoxHeader = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 14px;
  cursor: pointer;
  user-select: none;
  border-bottom: ${({ $isExpanded, theme }) =>
    $isExpanded
      ? `1px solid ${theme.name === 'short' ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.1)'}`
      : 'none'};
  background: ${({ theme }) =>
    theme.name === 'short' ? 'rgba(255, 255, 255, 0.07)' : 'rgba(0, 0, 0, 0.04)'};

  &:hover {
    background: ${({ theme }) =>
      theme.name === 'short' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)'};
  }
`;

export const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

export const HeaderIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const NewsBoxKicker = styled.span`
  font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New',
    monospace;
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  opacity: 0.58;
  line-height: 1.1;
`;

export const StatusDot = styled.span<{ $pulsing: boolean }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
  opacity: ${({ $pulsing }) => ($pulsing ? 1 : 0.38)};
  flex-shrink: 0;

  ${({ $pulsing }) => $pulsing && `animation: pulse 2s ease-in-out infinite;`}

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.35;
      transform: scale(0.92);
    }
  }
`;

export const NewsBoxTitle = styled.span`
  font-size: 0.86rem;
  letter-spacing: 0.02em;
  font-weight: 600;
  line-height: 1.15;
  white-space: nowrap;
`;

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const TimingMeta = styled.span`
  font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New',
    monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0.48;
  white-space: nowrap;
`;

export const NewsBoxToggle = styled.span<{ $isExpanded: boolean }>`
  font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New',
    monospace;
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ $isExpanded, theme }) => ($isExpanded ? 'inherit' : theme.text)};
  opacity: ${({ $isExpanded }) => ($isExpanded ? 0.72 : 0.92)};
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => (theme.name === 'short' ? 'rgba(255, 255, 255, 0.34)' : 'rgba(0, 0, 0, 0.26)')};
`;

export const PreviewSnippet = styled.p<{ $isExpanded: boolean }>`
  margin: 0;
  font-size: 0.68rem;
  line-height: 1.35;
  letter-spacing: 0.01em;
  opacity: ${({ $isExpanded }) => ($isExpanded ? 0.52 : 0.68)};
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${({ $isExpanded }) => ($isExpanded ? 1 : 2)};
  overflow: hidden;
`;

export const NewsBoxContent = styled.div`
  padding: 12px 14px 14px;
  overflow-y: auto;
  max-height: calc(min(58vh, 640px) - 90px);
`;

export const AnalysisMeta = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 0.6rem;
`;

export const Sentiment = styled.span<{ $sentiment: string }>`
  font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New',
    monospace;
  font-size: 0.62rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.22rem 0.55rem;
  border-radius: 999px;
  background: ${({ $sentiment }) =>
    $sentiment === 'bullish'
      ? 'rgba(74, 222, 128, 0.26)'
      : $sentiment === 'bearish'
      ? 'rgba(248, 113, 113, 0.26)'
      : 'rgba(255, 255, 255, 0.28)'};
  border: 1px solid ${({ $sentiment }) =>
    $sentiment === 'bullish' ? 'rgba(74, 222, 128, 0.56)' : $sentiment === 'bearish' ? 'rgba(248, 113, 113, 0.56)' : 'rgba(255, 255, 255, 0.48)'};
  color: inherit;
`;

export const Confidence = styled.span`
  font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New',
    monospace;
  font-size: 0.62rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0.58;
`;

export const Summary = styled.p`
  font-size: 0.78rem;
  line-height: 1.45;
  opacity: 0.92;
  margin: 0;
`;

export const MarkdownContent = styled.div`
  font-size: 0.76rem;
  line-height: 1.52;
  margin-top: 0.9rem;
  opacity: 0.9;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-size: 0.84rem;
    font-weight: 600;
    margin: 0.65rem 0 0.3rem;
  }

  p {
    margin: 0.3rem 0;
  }

  ul,
  ol {
    margin: 0.35rem 0;
    padding-left: 1.25rem;
  }

  li {
    margin: 0.16rem 0;
  }

  code {
    font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New',
      monospace;
    opacity: 0.82;
    padding: 0.08rem 0.28rem;
    border-radius: 4px;
    font-size: 0.7rem;
    background: ${({ theme }) =>
      theme.name === 'short' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.12)'};
  }

  pre {
    opacity: 0.95;
    padding: 0.55rem;
    overflow-x: auto;
    margin: 0.55rem 0;
    border-radius: 8px;
    background: ${({ theme }) =>
      theme.name === 'short' ? 'rgba(255, 255, 255, 0.18)' : 'rgba(0, 0, 0, 0.14)'};

    code {
      padding: 0;
      background: transparent;
    }
  }

  strong {
    font-weight: 600;
  }

  em {
    font-style: italic;
  }

  a {
    text-decoration: underline;

    &:hover {
      opacity: 0.72;
    }
  }
`;
